import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Box,
  Grid,
  Menu,
  Stack,
  Drawer,
  Dialog,
  MenuItem,
  MenuList,
  useTheme,
  Container,
  SpeedDial,
  Typography,
  ListItemIcon,
  ListItemText,
  useMediaQuery,
  DialogContent,
  SpeedDialAction,
  FormGroup,
  FormControlLabel,
  AppBar,
  Input,
  CircularProgress,
  Checkbox,
  debounce,
  InputAdornment,
  TablePagination,
} from '@mui/material';
import {
  Cancel, FileUpload, MoreHoriz, CreateNewFolderRounded,
  VideoCallRounded, CreateNewFolder, Settings, MoveDown,
} from '@mui/icons-material';

import { useSelector, useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
// import/no-extraneous-dependencies
import JsFileDownloader from 'js-file-downloader';

import {
  NoDataOverlay, PrimaryLoadingButton, PrimaryTextField, SecondaryButton,
} from 'components';
import { useConfirmation } from 'base/common';
import { getMediaPreviewUrl } from 'utils/file-uploader';
import {
  BoldSettingIcon,
  ExportIcon, SearchIcon, TrashFillIcon, UploadSquareFillIcon, ArrowRightIcon,
} from '../../assets/icons';
import MediaItem from './components/media-item';
import CreateMediaFolderForm from './components/create-media-folder-from';
import { ExpertHubLayout } from '../../components/templates';
import MyMediaFilters from './components/media-filters';
import './style.scss';
import UploadMediaModal from './components/upload-new-media';
import { mediaLibraryAction } from './slice';
import {
  selectCount, selectIsDownloadMediaEnabled, selectKeyword, selectMediaLoading,
  selectMedias, selectMediaTotal, selectOpenFolderSelect, selectPage,
  selectPath, selectPerPage, selectRenameMediaFormOpen, selectSelectedFiles, selectSortBy,
  selectStroage, selectType,
} from './selector';
import MediaItemLoading from './components/media-item-loading';
import { ExpertContent } from './interface';
import RecordingShareModel from './components/recording-share-model';
import RenameMediaForm from './components/rename-media-form';
import SelectFolderModel from './components/select-folder-model';

const MediaLibrary = () => {
  const [keyword, setKeyword] = useState('');
  const [urlPaths, setUrlPaths] = useState<string[]>([]);
  const [showFilters, setShowFilters] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [openCreateFolder, setOpenCreateFolder] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [editDetails, setEditDetails] = useState<ExpertContent>();

  const inputFileRef = useRef<HTMLInputElement>();
  const menuOpen = Boolean(menuAnchorEl);
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const confirmAlert = useConfirmation();

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const params: Record<string, string> = {};
    searchParams.forEach((value, key) => {
      params[key] = value;
    });

    const queryString = new URLSearchParams(params).toString();

    navigate(`/media-library/root?${queryString}`, { replace: true });
    dispatch(mediaLibraryAction.fetchRemainingStroage());
  }, [dispatch]);

  const openUploadModal = () => {
    setShowUploadModal(true);
  };

  const closeUploadModal = () => {
    setShowUploadModal(false);
  };

  const queryParams = new URLSearchParams(location.search);
  const [isOpenRecordingShareModel, setOpenRecordingShareModel] = React.useState(false);
  const [isMediaTrimming, setIsMediaTrimming] = React.useState(false);
  const [trimVideoPath, setTrimVideoPath] = React.useState<string | null>('');
  const [trimVideoId, setTrimVideoId] = React.useState<string | null>('');

  const openRecordingShareModel = queryParams.get('openRecordingShareModel');
  const meetingId = queryParams.get('meeting');
  const videoPath = queryParams.get('path');

  React.useEffect(() => {
    if (openRecordingShareModel && openRecordingShareModel === 'true') {
      setIsMediaTrimming(false);
      setOpenRecordingShareModel(true);
    }
  }, [openRecordingShareModel]);

  React.useEffect(() => {
    setTrimVideoPath(videoPath);
  }, [videoPath]);

  const isMdDown = useMediaQuery(theme.breakpoints.down('md'));
  const isSmDown = useMediaQuery(theme.breakpoints.down('sm'));

  const page = useSelector(selectPage);
  const rowsPerPage = useSelector(selectPerPage);
  const path = useSelector(selectPath);
  const count = useSelector(selectCount);
  const type = useSelector(selectType);
  const sortBy = useSelector(selectSortBy);
  const storeKeyword = useSelector(selectKeyword);
  const selectedFiles = useSelector(selectSelectedFiles);
  const results = useSelector(selectMedias);
  const total = useSelector(selectMediaTotal);
  const loading = useSelector(selectMediaLoading);
  const { allowedStorage, mediaStorageSize } = useSelector(selectStroage);
  const renameMediaFormOpen = useSelector(selectRenameMediaFormOpen);
  const openFolderSelect = useSelector(selectOpenFolderSelect);
  const isDownloadMediaEnabled = useSelector(selectIsDownloadMediaEnabled);

  const isAllSelected = useMemo(() => {
    if (loading) {
      return false;
    }
    if (results.length === 0) {
      return false;
    }
    return selectedFiles.length === results.length;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [results.length, selectedFiles.length]);

  const isRoot = useMemo(() => path === '/root', [path]);
  const menuOpenHandler = (event: React.MouseEvent<HTMLButtonElement>) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const menuCloseHandler = () => {
    setMenuAnchorEl(null);
  };

  const onFlitersClickHandler = () => {
    setShowFilters(true);
  };

  const onFiltersCloseHandler = () => {
    setShowFilters(false);
  };

  const downloadMediaFiles = async () => {
    dispatch(mediaLibraryAction.startLoading());
    // eslint-disable-next-line no-restricted-syntax
    for await (const selectedFile of selectedFiles) {
      const file = results.find(
        (f: ExpertContent) => f._id === selectedFile,
      )!;
      await new JsFileDownloader({
        url: file.contentUrl,
        filename: `${file.title}${file.fileType.startsWith('.') ? '' : '.'}${file.fileType.toLowerCase()}`,
      });
    }
    dispatch(mediaLibraryAction.endLoading());
  };

  const downloadSingleFile = async (file: ExpertContent) => {
    const fileName = `${file.title}${file.fileType.startsWith('.') ? '' : '.'}${file.fileType.toLowerCase()}`;

    try {
      const anchor = document.createElement('a');
      anchor.style.display = 'none';
      anchor.href = getMediaPreviewUrl(file.contentUrl, fileName, true);
      anchor.download = fileName;
      anchor.target = '_blank';
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
    } catch (error) {
      console.error('Error downloading file:', error);
    }
  };

  const copyLink = async (link: ExpertContent) => {
    try {
      await navigator.clipboard.writeText(link.contentUrl);
      dispatch(mediaLibraryAction.endLoading());
    } catch (error) {
      console.log('Cannot copy link', error);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const keywordDelayed = useCallback(
    debounce((value: string) => {
      dispatch(mediaLibraryAction.setKeyword(value));
    }, 300),
    [],
  );

  const onKeywordChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!loading) {
      dispatch(mediaLibraryAction.startLoading());
    }

    setKeyword(e.target.value);
    keywordDelayed(e.target.value);
  };

  const handleCreateFolderOpen = () => setOpenCreateFolder(true);
  const handleCreateFolderClose = () => setOpenCreateFolder(false);
  const handleFileMoveOpen = () => {
    dispatch(mediaLibraryAction.setOpenFolderSelect(true));
    dispatch(mediaLibraryAction.fetchFolderSelectorMedia());
  };
  const handleFolderCloseSelect = () => {
    if (isDownloadMediaEnabled === null) {
      dispatch(mediaLibraryAction.setSelectedTargetFolder(null));
    }
    dispatch(mediaLibraryAction.selectUndoButtonDisabled(true));
    dispatch(mediaLibraryAction.setisDownloadMediaEnabled(null));
    dispatch(mediaLibraryAction.setOpenFolderSelect(false));
  };
  const handleEditNameClose = () => dispatch(mediaLibraryAction.setRenameMediaFormOpen(false));

  const onNavigateBackHandler = () => {
    if (urlPaths.length > 0) {
      urlPaths.pop();
      navigate(`/media-library${urlPaths.length > 1 ? urlPaths[urlPaths.length - 1] : '/root'}`);
    }
  };

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    dispatch(mediaLibraryAction.setPage(newPage));
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    dispatch(mediaLibraryAction.setPerPage(parseInt(event.target.value, 10)));
    dispatch(mediaLibraryAction.setPage(0));
  };

  const handleCreateContent = () => {
    navigate('/media/record-content');
  };

  const onDeleteMediaHanlder = () => {
    confirmAlert({
      variant: 'confirm',
      icon: 'warning',
      message: 'Are you sure you want to delete this media?',
      confirmButtonTxt: 'Yes',
      cancelButtonTxt: 'Cancel',
    })
      .then(() => {
        dispatch(mediaLibraryAction.deleteSelectedMedia());
      });
  };

  useEffect(() => {
    setKeyword(storeKeyword);
  }, [storeKeyword]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [page, type, sortBy]);

  useEffect(() => {
    const currentRoute = decodeURI(location.pathname.replace('/media-library', '') as string);
    if (!urlPaths.includes(currentRoute) && currentRoute !== '/root') {
      setUrlPaths((preUrlPaths) => [...preUrlPaths, currentRoute]);
    } else {
      setUrlPaths(['/root']);
    }
    dispatch(mediaLibraryAction.setPath(currentRoute));
  }, [location, dispatch]);

  return (
    <>
      <ExpertHubLayout loading={false}>
        <div>
          <AppBar
            component="div"
            position="sticky"
            elevation={0}
            sx={{
              mb: 1,
              borderRadius: 0,
              top: {
                xs: '61px',
                md: '81px',
              },
              backgroundColor: 'background.default',
            }}
          >
            <Grid container justifyContent="space-between" sx={{ py: isMdDown ? 0.5 : 5 }}>
              <Grid
                item
                xs={12}
                md={3}
                lg={3}
                display={{ xs: 'flex', md: 'block' }}
                alignItems="center"
                justifyContent="space-between"
              >
                <Typography
                  variant="h4"
                  mb={1}
                  sx={{ color: 'text.primary' }}
                >
                  Your media
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight={600}
                  sx={{ cursor: 'pointer', color: 'text.secondary' }}
                >
                  {`${count} files (${(+mediaStorageSize).toFixed(2)} GB / ${(+allowedStorage).toFixed(2)} GB)`}
                </Typography>
              </Grid>
              <Grid
                item
                xs={12}
                md={9}
                lg={8}
                display={{ xs: 'none', md: 'flex' }}
                alignItems="center"
                justifyContent="space-between"
              >
                <FormGroup>
                  <FormControlLabel
                    control={<Checkbox />}
                    label={
                      <Typography variant="body1" sx={{ color: 'text.secondary' }}>Select all</Typography>
                    }
                    value="Select all"
                    checked={isAllSelected}
                    onChange={(e, value) => {
                      if (value) {
                        dispatch(mediaLibraryAction.selectAllFiles());
                      } else {
                        dispatch(mediaLibraryAction.deselectAllFiles());
                        dispatch(mediaLibraryAction.deselectFilesSelectedForUndo());
                      }
                    }}
                  />
                </FormGroup>
                <Stack direction="row" spacing={2}>
                  <Input
                    type="file"
                    inputRef={inputFileRef}
                    sx={{ display: 'none' }}
                  />
                  {!isRoot && (
                    <SecondaryButton
                      variant="outlined"
                      sx={{ px: 0 }}
                      onClick={onNavigateBackHandler}
                    >
                      <ArrowRightIcon sx={{ transform: 'rotate(180deg)' }} />
                    </SecondaryButton>
                  )}
                  <PrimaryLoadingButton
                    onClick={openUploadModal}
                    disabled={isUploading}
                  >
                    {isUploading && <CircularProgress size={20} />}
                    {!isUploading && (
                      <>
                        <ExportIcon sx={{ mr: 1 }} />
                        Upload new media
                      </>
                    )}
                  </PrimaryLoadingButton>
                  <div className="d-none d-lg-block">
                    <SecondaryButton onClick={handleCreateContent}>
                      <>
                        <VideoCallRounded sx={{ mr: 1 }} />
                        Create content
                      </>
                    </SecondaryButton>
                  </div>
                  <SecondaryButton onClick={menuOpenHandler}>
                    <MoreHoriz sx={{ fontSize: 27 }} />
                  </SecondaryButton>
                </Stack>
              </Grid>
            </Grid>
          </AppBar>

          <Grid container rowSpacing={isMdDown ? 1 : 6} justifyContent="space-between">
            <Grid item xs={12} md={3} lg={3}>
              <Stack spacing={isMdDown ? 1 : 5}>
                {isMdDown && !isRoot && (
                  <SecondaryButton
                    variant="outlined"
                    sx={{ px: 0 }}
                    onClick={onNavigateBackHandler}
                  >
                    <ArrowRightIcon sx={{ transform: 'rotate(180deg)', mr: 1 }} />
                    {' '}
                    Back
                  </SecondaryButton>
                )}
                <Box mb={{ xs: 3, md: 0 }} display="flex" gap={2}>
                  <PrimaryTextField
                    fullWidth
                    placeholder="Search"
                    value={keyword}
                    onChange={onKeywordChangeHandler}
                    InputProps={{
                      startAdornment: isSmDown && (
                        <SearchIcon
                          sx={{ fontSize: { xs: 20 }, mr: 2 }}
                        />
                      ),
                      endAdornment: !isSmDown && (
                        <SearchIcon sx={{ fontSize: { xs: 20 } }} />
                      ),
                    }}
                    endAdornment={(
                      <InputAdornment position="end">
                        <SearchIcon />
                      </InputAdornment>
                    )}
                  />

                  <Box display={{ md: 'none' }} height="100%">
                    <Box
                      sx={{
                        height: 40,
                        display: 'flex',
                        alignItems: 'center',
                        p: isSmDown ? 1 : 2.5,
                        background: (t) => t.palette.grey.A400,
                        border: (t) => `1px solid ${t.palette.grey.A100}`,
                        borderRadius: (t) => t.custom.borderRadius,
                        marginTop: 1,
                      }}
                      onClick={onFlitersClickHandler}
                    >
                      <BoldSettingIcon
                        sx={{
                          fontSize: { xs: 20 },
                          color: (t) => t.palette.primary.main,
                        }}
                      />
                    </Box>
                  </Box>
                </Box>

                {isMdDown && selectedFiles && selectedFiles.length > 0 && (
                  <Box display="flex" gap={2} sx={{ justifyContent: 'end' }}>
                    <SecondaryButton
                      variant="outlined"
                      onClick={downloadMediaFiles}
                      sx={{ mb: 2 }}
                    >
                      <UploadSquareFillIcon sx={{ mr: 1, color: (t) => t.palette.primary.main }} />
                      {' '}
                      Download
                    </SecondaryButton>
                    <SecondaryButton
                      variant="outlined"
                      onClick={() => {
                        onDeleteMediaHanlder();
                      }}
                      sx={{ mb: 2 }}
                    >
                      <TrashFillIcon sx={{ mr: 1, color: (t) => t.palette.primary.main }} />
                      {' '}
                      Delete
                    </SecondaryButton>
                  </Box>
                )}

                <Box display={{ xs: 'none', md: 'block' }}>
                  <MyMediaFilters />
                </Box>
              </Stack>
            </Grid>

            <Grid item xs={12} md={9} lg={8}>
              {loading && (
                <Grid
                  container
                  columnSpacing={{ xs: 4, md: 3 }}
                  rowSpacing={{ xs: 4, md: 6 }}
                >
                  {new Array(8).fill('').map((_, index) => (
                    <Grid item xs={6} sm={4} md={3} key={Math.random()}>
                      <MediaItemLoading />
                    </Grid>
                  ))}
                </Grid>
              )}

              {!loading && results.length > 0 && (
                <Grid
                  container
                  columnSpacing={{ xs: 4, md: 3 }}
                  rowSpacing={{ xs: 4, md: 6 }}
                >
                  {results.map((media, index) => (
                    <Grid item xs={6} sm={4} md={3} key={media._id}>
                      <MediaItem
                        media={media}
                        downloadSingleFile={downloadSingleFile}
                        copyLink={copyLink}
                        setEditDetails={setEditDetails}
                        openTrimModel={(video: ExpertContent) => {
                          setIsMediaTrimming(true);
                          setOpenRecordingShareModel(true);
                          setTrimVideoPath(video.contentUrl);
                          setTrimVideoId(video._id);
                        }}
                      />
                    </Grid>
                  ))}
                </Grid>
              )}
              {!loading && results.length === 0 && (
                <NoDataOverlay message="No media items found" />
              )}

              {!loading && results.length > 0 && (
                <div className="row my-3">
                  <div className="col-12 d-flex flex-column align-items-center justify-content-center">
                    <TablePagination
                      component="div"
                      count={total}
                      page={page}
                      rowsPerPageOptions={[12, 16, 20, 24]}
                      labelRowsPerPage="Items per page"
                      onPageChange={handleChangePage}
                      rowsPerPage={rowsPerPage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                  </div>
                </div>
              )}
            </Grid>
          </Grid>
        </div>
      </ExpertHubLayout>

      <Drawer
        anchor="bottom"
        open={showFilters}
        onClose={onFiltersCloseHandler}
        sx={{
          backgroundColor: 'transparent',
          '.MuiBackdrop-root': {
            opacity: '0 !important',
          },
          '.MuiDrawer-paper': {
            borderRadius: 0,
            position: 'absolute',
            top: '60px',
            background: (t) => t.palette.background.default,
          },
        }}
      >
        <Container sx={{ py: 2 }}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 4, mx: 2 }}
          >
            <Typography variant="subtitle1">Filters</Typography>
            <Cancel onClick={onFiltersCloseHandler} />
          </Box>

          <div className="mx-3"><MyMediaFilters /></div>
        </Container>
      </Drawer>

      <Menu
        anchorEl={menuAnchorEl}
        open={menuOpen}
        onClose={menuCloseHandler}
        sx={{ ul: { width: 187 } }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuList>
          <MenuItem onClick={() => {
            handleCreateFolderOpen();
            menuCloseHandler();
          }}
          >
            <ListItemIcon>
              <CreateNewFolderRounded
                sx={{ fontSize: 20, color: 'primary.main' }}
              />
            </ListItemIcon>
            <ListItemText>
              <Typography fontSize={{ xs: 14 }}>Create folder</Typography>
            </ListItemText>
          </MenuItem>
          <MenuItem
            disabled={!selectedFiles || selectedFiles.length === 0}
            onClick={() => {
              handleFileMoveOpen();
              menuCloseHandler();
            }}
          >
            <ListItemIcon>
              <MoveDown
                sx={{ fontSize: 20, color: 'primary.main' }}
              />
            </ListItemIcon>
            <ListItemText>
              <Typography fontSize={{ xs: 14 }}>Move</Typography>
            </ListItemText>
          </MenuItem>
          <MenuItem
            onClick={() => {
              downloadMediaFiles();
              menuCloseHandler();
            }}
            disabled={!selectedFiles || selectedFiles.length === 0}
          >
            <ListItemIcon>
              <UploadSquareFillIcon
                sx={{ fontSize: 20, color: (t) => t.palette.primary.main }}
              />
            </ListItemIcon>
            <ListItemText>
              <Typography fontSize={{ xs: 14 }}>Download</Typography>
            </ListItemText>
          </MenuItem>
          <MenuItem
            onClick={() => {
              onDeleteMediaHanlder();
              menuCloseHandler();
            }}
            disabled={!selectedFiles || selectedFiles.length === 0}
          >
            <ListItemIcon>
              <TrashFillIcon
                sx={{ fontSize: 20, color: (t) => t.palette.primary.main }}
              />
            </ListItemIcon>
            <ListItemText>
              <Typography fontSize={{ xs: 14 }}>Delete</Typography>
            </ListItemText>
          </MenuItem>
        </MenuList>
      </Menu>

      <SpeedDial
        ariaLabel="media speed dial"
        sx={{
          position: 'fixed',
          bottom: 16,
          right: 16,
          display: { md: 'none' },
        }}
        icon={<Settings />}
      >
        <SpeedDialAction
          sx={{
            boxShadow: '0px 2px 4px 0px #353535',
          }}
          icon={
            isUploading ? <CircularProgress size={20} /> : <FileUpload sx={{ color: 'primary.main' }} />
          }
          tooltipTitle="Upload new media"
          onClick={openUploadModal}
        />
        <SpeedDialAction
          sx={{
            boxShadow: '0px 2px 4px 0px #353535',
          }}
          icon={
            <CreateNewFolder sx={{ color: 'primary.main' }} />
          }
          tooltipTitle="Create folder"
          onClick={handleCreateFolderOpen}
        />
      </SpeedDial>

      <Dialog
        open={openCreateFolder}
        onClose={handleCreateFolderClose}
        sx={{ backdropFilter: 'blur(4px)' }}
      >
        <DialogContent>
          <CreateMediaFolderForm path={path} onComplete={handleCreateFolderClose} />
        </DialogContent>
      </Dialog>

      <UploadMediaModal path={path} open={showUploadModal} onClose={closeUploadModal} />
      <SelectFolderModel
        open={openFolderSelect}
        onClose={handleFolderCloseSelect}
      />

      <Dialog
        open={renameMediaFormOpen}
        onClose={handleEditNameClose}
        sx={{ backdropFilter: 'blur(4px)' }}
      >
        <DialogContent>
          <RenameMediaForm media={editDetails} />
        </DialogContent>
      </Dialog>

      <RecordingShareModel
        isOpen={isOpenRecordingShareModel}
        onClose={() => setOpenRecordingShareModel(false)}
        trimVideoPath={trimVideoPath ?? ''}
        trimVideoId={trimVideoId ?? ''}
        meetingId={meetingId}
        isMediaTrimming={isMediaTrimming}
      />
    </>
  );
};

export default MediaLibrary;
